/**
 * Gateway module for WebSocket communication (OUTBOUND - Sending messages)
 * Handles real-time message streaming via WebSocket with priority queues
 *
 * This is a TypeScript replica of backend gateway.py
 * 
 * Responsibilities:
 * - Connection and CommunicationManager for sending messages
 * - Priority queues (high/low) for message sending
 * - initializeWebSocketConnection() - entry point for establishing WebSocket
 * - Sends PING messages via health check system
 */

import { buildWireGithubSettings } from '../../githubWireSettings';

// Note: WebSocketRouter and initializeWebSocketConnection have been moved to connection.ts

// Priority channel definitions
export const HIGH_CHANNELS = new Set(["chat", "discovery", "health"]);  // always snappy
export const MID_CHANNELS = new Set(["build_feature", "build_epic"]);     // important but can lag slightly
export const LOW_CHANNELS = new Set(["logs"]);                          // noisy

// Type definitions
export const ACTORS = ["user", "coder", "system"] as const;
export const MESSAGE_TYPES = ["request", "response"] as const;
export const CHANNELS = ["health", "chat", "discovery", "build_feature", "build_epic", "deploy", "logs"] as const;
export const STATUSES = ["streaming", "completed", "error", "connected"] as const;
export const BODY_KINDS = ["logs", "contract", "messages", "message", "status", "error", "sync_status"] as const;

export type Actor = typeof ACTORS[number];
export type MessageType = typeof MESSAGE_TYPES[number];
export type Channel = typeof CHANNELS[number];
export type Status = typeof STATUSES[number];
export type BodyKind = typeof BODY_KINDS[number];

/**
 * Check if VishMakerPayload is high priority (HIGH or MID channels)
 */
export function isHighPriority(payload: VishMakerPayload): boolean {
    return HIGH_CHANNELS.has(payload.channel) || MID_CHANNELS.has(payload.channel);
}

/**
 * Check if VishMakerPayload is low priority
 */
export function isLowPriority(payload: VishMakerPayload): boolean {
    return LOW_CHANNELS.has(payload.channel);
}

// Interfaces
export interface Origin {
    threadId: string;
    originMessageId: string;
    originActor: Actor;
    origints: string;
    userId?: string;
    projectId?: string;
    featureId?: string;
    page?: string;
}

export interface Reply {
    parentMessageId: string;
    parentActor: Actor;
    parentts: string;
}

export interface Body {
    kind: BodyKind;
    payload: Record<string, any>;
}

/**
 * Simplified payload structure for VishMaker communication.
 *
 * Structure:
 * {
 *   "v": "1.0",
 *   "id": "uuid",
 *   "ts": "ISO-8601",
 *   "actor": "user|coder|system",
 *   "type": "request|response",
 *   "channel": "health|chat|discovery|build_feature|deploy|logs",
 *   "status": "streaming|completed|error|connected",
 *   "origin": {
 *     "threadId": "thread-id",
 *     "originMessageId": "uuid",
 *     "originActor": "user|coder|system",
 *     "origints": "ISO-8601",
 *     "userId": "optional",
 *     "projectId": "optional",
 *     "featureId": "optional",
 *     "page": "optional"
 *   },
 *   "reply": {
 *     "parentMessageId": "uuid",
 *     "parentActor": "user|coder|system",
 *     "parentts": "ISO-8601"
 *   },
 *   "body": {
 *     "kind": "logs|contract|messages|status|error",
 *     "payload": {}
 *   }
 * }
 *
 * status:
 *     streaming: The backend is streaming data to the frontend.
 *     completed : The backend has completed the request.
 *     error : The backend has encountered an error.
 *     connected : The backend is connected to the frontend.
 *
 * for discovery -> type: request, kind : messages, and payload : messages
 *     type: response, kind: messages, payload : messages
 *     type: response, kind: error, payload : error
 * for build_feature -> type: request, kind : contract, and payload : requirements
 *     type: response, kind: status, payload : status
 *     type: response, kind: error, payload : error
 * for chat -> type: request, kind : messages, and payload : messages
 *     type: response, kind: messages, payload : messages
 *     type: response, kind: error, payload : error
 * for logs -> type: log, kind : logs, and payload : logs
 *     type: response, kind: logs, payload : logs
 *     type: response, kind: error, payload : error
 */
export interface VishMakerPayload {
    v: string;
    id: string;
    ts: string;
    actor: Actor;
    type: MessageType;
    channel: Channel;
    status: Status;
    origin: Origin;
    reply?: Reply;
    body: Body;
}

/**
 * Channel context stored for request/response correlation
 */
interface ChannelContext {
    v: string;
    type: MessageType;
    origin: Origin;
    channel: Channel;
    reply: Reply;
    body: Body;
}

/**
 * Validate that incoming data follows VishMakerPayload structure.
 *
 * @param data - Dictionary to validate
 * @returns Tuple of [isValid, errorMessage, parsedPayload]
 */
export function validateVishmakerPayload(data: Record<string, any>): [boolean, string | null, VishMakerPayload | null] {
    const requiredFields = ["v", "id", "ts", "actor", "type", "channel", "status", "origin", "body"];

    // Check required fields
    const missingFields = requiredFields.filter(field => !(field in data));
    if (missingFields.length > 0) {
        return [false, `Missing required fields: ${missingFields.join(", ")}`, null];
    }

    // Validate field types
    if (typeof data.v !== "string") {
        return [false, "Field 'v' must be a string", null];
    }
    if (typeof data.id !== "string") {
        return [false, "Field 'id' must be a string", null];
    }
    if (typeof data.ts !== "string") {
        return [false, "Field 'ts' must be a string", null];
    }
    if (typeof data.actor !== "string") {
        return [false, "Field 'actor' must be a string", null];
    }
    if (typeof data.type !== "string") {
        return [false, "Field 'type' must be a string", null];
    }
    if (typeof data.channel !== "string") {
        return [false, "Field 'channel' must be a string", null];
    }
    if (typeof data.status !== "string") {
        return [false, "Field 'status' must be a string", null];
    }
    if (typeof data.origin !== "object" || data.origin === null) {
        return [false, "Field 'origin' must be an object", null];
    }
    if (typeof data.body !== "object" || data.body === null) {
        return [false, "Field 'body' must be an object", null];
    }

    // Validate origin fields
    const origin = data.origin;
    if (!("threadId" in origin) || typeof origin.threadId !== "string") {
        return [false, "Field 'origin.threadId' is required and must be a string", null];
    }
    if (!("originActor" in origin) || typeof origin.originActor !== "string") {
        return [false, "Field 'origin.originActor' is required and must be a string", null];
    }

    // Validate body fields
    const body = data.body;
    if ("kind" in body && typeof body.kind !== "string") {
        return [false, "Field 'body.kind' must be a string if present", null];
    }
    if (!("payload" in body)) {
        return [false, "Field 'body.payload' is required", null];
    }

    // reply is optional
    if ("reply" in data && typeof data.reply !== "object") {
        return [false, "Field 'reply' must be an object if present", null];
    }

    return [true, null, data as VishMakerPayload];
}

/**
 * WebSocket connection with prioritized message queues.
 * Manages high/low priority queues and writer loop.
 */
export class Connection {
    public websocket: WebSocket;
    public connectionId: string;
    public lastSeen: number;

    // Priority queues for outbound messages (pre-serialized JSON strings)
    public highQueue: string[] = [];
    public lowQueue: string[] = [];

    private writerTask: number | null = null;
    private isWriterRunning = false;

    // Streaming channels: inbox per channel
    public channelInbox: Map<Channel, any[]> = new Map();

    // Store per-channel request context
    public channelContext: Map<Channel, ChannelContext> = new Map();
    public readonly v = "1.0";

    constructor(websocket: WebSocket, connectionId: string) {
        this.websocket = websocket;
        this.connectionId = connectionId;
        this.lastSeen = Date.now();
    }

    /**
     * Update last seen timestamp
     */
    touch(): void {
        this.lastSeen = Date.now();
    }

    /**
     * Store request context for a specific channel.
     * This context will be automatically applied to responses for this channel.
     */
    setChannelContext(
        channel: Channel,
        origin: Origin,
        messageId: string,
        actor: Actor,
        ts: string
    ): void {
        this.channelContext.set(channel, {
            v: this.v,
            type: "response",
            origin: origin,
            channel: channel,
            reply: {
                parentMessageId: messageId,
                parentActor: actor,
                parentts: ts
            },
            body: {
                kind: "messages",
                payload: {}
            }
        });
    }

    /**
     * Retrieve stored request context for a channel
     */
    getChannelContext(channel: Channel): ChannelContext | undefined {
        return this.channelContext.get(channel);
    }

    /**
     * Clear stored request context for a channel
     */
    clearChannelContext(channel: Channel): void {
        this.channelContext.delete(channel);
    }

    /**
     * Start the writer loop if not already running
     */
    startWriter(): void {
        if (this.isWriterRunning) {
            return;
        }
        this.isWriterRunning = true;
        this.writerLoop();
    }

    /**
     * Always drain high priority first.
     * Then allow some low-priority throughput so logs still flow.
     * Queue contains pre-serialized JSON strings for efficiency.
     */
    private writerLoop(): void {
        const LOW_BURST = 30; // how many low msgs to send per cycle

        try {
            // 1) drain high quickly
            while (this.highQueue.length > 0) {
                const jsonStr = this.highQueue.shift()!;
                // Log health channel messages being sent
                try {
                    const parsed = JSON.parse(jsonStr);
                    if (parsed.channel === 'health') {
                        //console.log(`📡 [Health Check] Sending health message over WebSocket (HIGH PRIORITY)`);
                        //console.log(`   └─ WebSocket readyState: ${this.websocket.readyState} (${['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][this.websocket.readyState]})`);
                        //console.log(`   └─ Complete payload being sent:`, JSON.stringify(parsed, null, 2));
                        //console.log(`   └─ Payload breakdown:`);
                        //console.log(`      • id: ${parsed.id}`);
                        //console.log(`      • ts: ${parsed.ts}`);
                        //console.log(`      • actor: ${parsed.actor}`);
                        //console.log(`      • status: ${parsed.status}`);
                        //console.log(`      • type: ${parsed.type}`);
                        //console.log(`      • channel: ${parsed.channel}`);
                        //console.log(`      • origin:`, JSON.stringify(parsed.origin, null, 2));
                        //console.log(`      • body.kind: ${parsed.body?.kind}`);
                        //console.log(`      • body.payload:`, JSON.stringify(parsed.body?.payload, null, 2));
                        //console.log(`   └─ Raw JSON string (${jsonStr.length} bytes):`, jsonStr);
                    }
                } catch (e) {
                    // Ignore parse errors for logging
                }
                this.websocket.send(jsonStr);
            }

            // 2) send some low
            let sentLow = 0;
            while (sentLow < LOW_BURST && this.lowQueue.length > 0) {
                const jsonStr = this.lowQueue.shift()!;
                // Log health channel messages being sent
                try {
                    const parsed = JSON.parse(jsonStr);
                    if (parsed.channel === 'health') {
                        console.log(`📡 [Health Check] Sending health message over WebSocket (LOW PRIORITY)`);
                        console.log(`   └─ WebSocket readyState: ${this.websocket.readyState} (${['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][this.websocket.readyState]})`);
                        console.log(`   └─ Complete payload being sent:`, JSON.stringify(parsed, null, 2));
                        console.log(`   └─ Payload breakdown:`);
                        console.log(`      • id: ${parsed.id}`);
                        console.log(`      • ts: ${parsed.ts}`);
                        console.log(`      • actor: ${parsed.actor}`);
                        console.log(`      • status: ${parsed.status}`);
                        console.log(`      • type: ${parsed.type}`);
                        console.log(`      • channel: ${parsed.channel}`);
                        console.log(`      • origin:`, JSON.stringify(parsed.origin, null, 2));
                        console.log(`      • body.kind: ${parsed.body?.kind}`);
                        console.log(`      • body.payload:`, JSON.stringify(parsed.body?.payload, null, 2));
                        console.log(`   └─ Raw JSON string (${jsonStr.length} bytes):`, jsonStr);
                    }
                } catch (e) {
                    // Ignore parse errors for logging
                }
                this.websocket.send(jsonStr);
                sentLow++;
            }

            // If nothing to send, exit (writer will restart on next send)
            if (this.highQueue.length === 0 && this.lowQueue.length === 0) {
                this.isWriterRunning = false;
                return;
            }

            // Schedule next iteration
            this.writerTask = window.setTimeout(() => this.writerLoop(), 0);

        } catch (e) {
            console.log(`🔌 writer stopped (${this.connectionId}): ${String(e).slice(0, 120)}`);
            this.isWriterRunning = false;
        }
    }

    /**
     * Stop the writer loop
     */
    stopWriter(): void {
        if (this.writerTask !== null) {
            clearTimeout(this.writerTask);
            this.writerTask = null;
        }
        this.isWriterRunning = false;
    }
}

/**
 * Manages communication flow: orchestration → websocket.
 * Centralizes serialization and ensures reliable message delivery.
 */
export class CommunicationManager {
    private conn: Connection;

    constructor(connection: Connection) {
        this.conn = connection;
    }

    /**
     * Get the underlying connection
     */
    getConnection(): Connection {
        return this.conn;
    }

    /**
     * Universal emit method - single entry point for all frontend message sending.
     *
     * Gets base structure (v, type, origin, channel, reply) from channel context,
     * then adds dynamic fields (id, ts, actor, status, body) from parameters.
     *
     * Flow:
     * 1. Get channel context (base structure)
     * 2. Add dynamic fields from parameters
     * 3. Create VishMakerPayload
     * 4. Serialize once (efficient)
     * 5. Check priority based on channel
     * 6. Add to appropriate priority queue
     * 7. Start writer loop if needed
     *
     * @param channel - Channel name (health|chat|discovery|build_feature|logs)
     * @param actor - Actor sending the message (e.g., "user", "system")
     * @param status - Status value (streaming|completed|error|connected)
     * @param kind - Body kind (logs|contract|messages|status|error)
     * @param payload - Body payload data
     * @param messageType - Optional message type (defaults to context type or "request")
     * @returns true if emitted successfully, false otherwise
     *
     * @example
     * // Send a message
     * comm.emit("chat", "user", "connected", "messages", { text: "Hello" });
     *
     * // Send a build request
     * comm.emit("build_feature", "user", "streaming", "contract", { requirements: {...} });
     */
    emit(
        channel: Channel,
        actor: Actor,
        status: Status,
        kind: BodyKind,
        payload: Record<string, any>,
        messageType?: MessageType
    ): boolean {
        // 1) Get full channel context object (base structure)
        const channelCtx = this.conn.getChannelContext(channel);
        if (!channelCtx) {
            console.error(`No channel context found for channel: ${channel}`);
            return false;
        }

        // 2) Create a new object from channel context (don't mutate stored context)
        const payloadDict: Record<string, any> = { ...channelCtx };

        // 3) Add dynamic fields from parameters
        payloadDict.id = crypto.randomUUID();
        payloadDict.ts = this.getCurrentTimestamp();
        payloadDict.actor = actor;
        payloadDict.status = status;
        payloadDict.type = messageType || channelCtx.type || "request";
        if (channel === 'chat') {
            const projectId = channelCtx.origin?.projectId;
            payloadDict.body = {
                kind: kind,
                payload: {
                    ...payload,
                    settings: buildWireGithubSettings(projectId),
                },
            };
        } else {
            payloadDict.body = {
                kind: kind,
                payload: payload,
            };
        }

        // 4) Cast to VishMakerPayload
        const vishmakerPayload = payloadDict as VishMakerPayload;

        // Log full payload sent to backend (wire format) for chat and build_feature
        if (channel === 'chat') {
            console.log('📤 [Chat] Full VishMakerPayload sent to backend:', JSON.stringify(vishmakerPayload, null, 2));
        }
        if (channel === 'build_feature') {
            console.log('📤 [Build Feature] Full payload sent to VishCoder:', JSON.stringify(vishmakerPayload, null, 2));
        }

        // 5) Serialize ONCE
        const jsonStr = JSON.stringify(vishmakerPayload);
        
        // 6) Determine priority
        const isHigh = isHighPriority(vishmakerPayload);

        // 7) Add to appropriate priority queue
        try {
            if (isHigh) {
                this.conn.highQueue.push(jsonStr);
            } else {
                this.conn.lowQueue.push(jsonStr);
            }
        } catch (e) {
            console.warn(`Queue full, dropping message: ${channel}`);
            return false;
        }

        // 8) Start writer loop if needed
        this.conn.startWriter();
        return true;
    }

    /**
     * Get current timestamp in ISO format
     */
    getCurrentTimestamp(): string {
        return new Date().toISOString();
    }
}

/**
 * Generate a draft project ID in the format draft_{uuid[:5]}
 */
export function generateDraftProjectId(): string {
    const uuid = crypto.randomUUID().replace(/-/g, '');
    return `draft_${uuid.slice(0, 5)}`;
}

/**
 * Generate a unique thread ID
 */
export function generateThreadId(identifier: string): string {
    const timestamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0];
    return `thread_${identifier}_${timestamp}`;
}

/**
 * Create a new Connection and CommunicationManager from a WebSocket
 */
export function createCommunicationManager(websocket: WebSocket, connectionId?: string): CommunicationManager {
    const connId = connectionId || crypto.randomUUID();
    const connection = new Connection(websocket, connId);
    return new CommunicationManager(connection);
}

// Note: WebSocketRouter, initializeWebSocketConnection, and related functions have been moved to connection.ts
